import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Plus, RefreshCw, UploadCloud, Clock, FileText, Search,
} from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
    PRODUCTO_LABEL, ESTADO_LABEL, ESTADO_BADGE, fmtMXN, fmtDate, slaColor,
    PRODUCTOS,
} from "./_common";

export default function Pipeline() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtroEstado, setFiltroEstado] = useState("__all");
    const [filtroProducto, setFiltroProducto] = useState("__all");
    const [q, setQ] = useState("");
    const [openLeads, setOpenLeads] = useState(false);
    const [leads, setLeads] = useState([]);
    const [leadId, setLeadId] = useState("");
    const [leadProducto, setLeadProducto] = useState("P-GAR");
    const [leadComplejidad, setLeadComplejidad] = useState("estandar");

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filtroEstado !== "__all") params.estado = filtroEstado;
            if (filtroProducto !== "__all") params.producto = filtroProducto;
            const { data } = await api.get("/aceptacion/pipeline/solicitudes", { params });
            setRows(data || []);
        } catch (err) {
            toast.error("No se pudo cargar el pipeline", {
                description: err?.response?.data?.detail,
            });
        } finally {
            setLoading(false);
        }
    };

    const loadLeads = async () => {
        try {
            const { data } = await api.get("/admin/leads", { params: { status: "nuevo", limit: 100 } });
            setLeads(data || []);
        } catch (err) {
            toast.error("No se pudieron cargar los leads");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroEstado, filtroProducto]);

    const filteredRows = rows.filter((r) => {
        if (!q) return true;
        const Q = q.toLowerCase();
        return (
            r.folio?.toLowerCase().includes(Q) ||
            r.solicitante?.razon_social?.toLowerCase().includes(Q) ||
            r.solicitante?.rfc?.toLowerCase().includes(Q)
        );
    });

    const promover = async () => {
        if (!leadId) {
            toast.error("Selecciona un lead");
            return;
        }
        try {
            const { data } = await api.post(
                `/aceptacion/pipeline/solicitudes/desde-lead/${leadId}` +
                `?complejidad=${leadComplejidad}&producto=${leadProducto}`,
            );
            toast.success("Lead promovido a solicitud", {
                description: `Folio ${data.folio}`,
            });
            setOpenLeads(false);
            navigate(`/aceptacion/solicitudes/${data.folio}`);
        } catch (err) {
            toast.error("No se pudo promover", {
                description: err?.response?.data?.detail,
            });
        }
    };

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1600px]" data-testid="pipeline-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl text-[color:var(--brand-charcoal)]">
                        Pipeline de Aceptación · G-02
                    </h1>
                    <p className="text-sm text-[color:var(--brand-charcoal)]/70 mt-1">
                        Solicitudes F-01 en curso con SLA 10 días hábiles (estándar) · 20 (complejas).
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={load} disabled={loading} data-testid="pipeline-refresh">
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                        Refrescar
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => { loadLeads(); setOpenLeads(true); }}
                        data-testid="pipeline-promover-lead"
                    >
                        <UploadCloud className="h-4 w-4 mr-2" /> Promover desde Lead
                    </Button>
                    <Button
                        onClick={() => navigate("/aceptacion/solicitudes/nueva")}
                        data-testid="pipeline-nueva"
                    >
                        <Plus className="h-4 w-4 mr-2" /> Nueva solicitud
                    </Button>
                </div>
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap gap-3 items-center">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        placeholder="Buscar folio / razón social / RFC..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 w-80"
                        data-testid="pipeline-search"
                    />
                </div>
                <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                    <SelectTrigger className="w-56" data-testid="pipeline-filtro-estado">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Todos los estados</SelectItem>
                        {Object.entries(ESTADO_LABEL).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={filtroProducto} onValueChange={setFiltroProducto}>
                    <SelectTrigger className="w-64" data-testid="pipeline-filtro-producto">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Todos los productos</SelectItem>
                        {PRODUCTOS.map((p) => (
                            <SelectItem key={p.code} value={p.code}>{p.code} · {p.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="text-sm text-gray-500 ml-auto">
                    {filteredRows.length} solicitudes
                </div>
            </div>

            {/* Tabla */}
            <div className="rounded-lg border border-gray-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[color:var(--brand-charcoal)] text-[color:var(--brand-paper)]">
                        <tr>
                            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Folio</th>
                            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Solicitante</th>
                            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Producto</th>
                            <th className="text-right px-4 py-3 text-xs uppercase tracking-wider">Patrimonio</th>
                            <th className="text-center px-4 py-3 text-xs uppercase tracking-wider">Etapa</th>
                            <th className="text-center px-4 py-3 text-xs uppercase tracking-wider">Días</th>
                            <th className="text-center px-4 py-3 text-xs uppercase tracking-wider">SLA</th>
                            <th className="text-center px-4 py-3 text-xs uppercase tracking-wider">Estado</th>
                            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Responsable</th>
                            <th className="text-right px-4 py-3 text-xs uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRows.length === 0 && (
                            <tr>
                                <td colSpan={10} className="text-center text-gray-500 py-12">
                                    {loading ? "Cargando..." : "No hay solicitudes con estos filtros"}
                                </td>
                            </tr>
                        )}
                        {filteredRows.map((r, i) => {
                            const pasoActual = (r.pasos || []).filter((p) => p.estado === "completado").length;
                            const slaDias = r.sla_dias_habiles || (r.complejidad === "compleja" ? 20 : 10);
                            const folio = r.folio_f01 || r.id;
                            return (
                                <tr
                                    key={r.id}
                                    className={`border-t border-gray-100 hover:bg-[color:var(--brand-paper)]/50 cursor-pointer ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                                    onClick={() => navigate(`/aceptacion/solicitudes/${folio}`)}
                                    data-testid={`pipeline-row-${folio}`}
                                >
                                    <td className="px-4 py-3 font-mono text-xs">{folio}</td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium">{r.solicitante?.razon_social}</div>
                                        <div className="text-xs text-gray-500">{r.solicitante?.rfc}</div>
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        <span className="font-mono">{r.producto_solicitado}</span>
                                        <div className="text-gray-500">{PRODUCTO_LABEL[r.producto_solicitado]}</div>
                                    </td>
                                    <td className="px-4 py-3 text-right font-variant-numeric tabular-nums">
                                        {fmtMXN(r.patrimonio_estimado_mxn)}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className="font-mono text-xs">{pasoActual}/7</span>
                                    </td>
                                    <td className="px-4 py-3 text-center text-xs">
                                        {r.dias_en_proceso ?? "—"}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${slaColor(r.dias_en_proceso, r.dentro_de_sla, slaDias)}`}>
                                            <Clock className="h-3 w-3" />
                                            {r.dentro_de_sla === false ? "Vencido" : `${slaDias - (r.dias_en_proceso || 0)}d`}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`text-xs px-2 py-0.5 rounded border ${ESTADO_BADGE[r.estado_general] || "bg-gray-100"}`}>
                                            {ESTADO_LABEL[r.estado_general] || r.estado_general}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {r.responsable_asignado ? (
                                            <span className="text-gray-700">{r.responsable_asignado.split("@")[0]}</span>
                                        ) : (
                                            <span className="text-gray-400 italic">sin asignar</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <Button size="sm" variant="ghost" onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/aceptacion/solicitudes/${folio}`);
                                        }}>
                                            <FileText className="h-3.5 w-3.5" />
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Modal promover desde lead */}
            <Dialog open={openLeads} onOpenChange={setOpenLeads}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Promover lead a solicitud F-01</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-500">Lead</label>
                            <Select value={leadId} onValueChange={setLeadId}>
                                <SelectTrigger data-testid="modal-lead-select">
                                    <SelectValue placeholder={leads.length ? "Selecciona..." : "No hay leads nuevos"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {leads.map((l) => (
                                        <SelectItem key={l.id} value={l.id}>
                                            {l.empresa} — {l.nombre} ({l.score})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs uppercase tracking-wider text-gray-500">Producto</label>
                                <Select value={leadProducto} onValueChange={setLeadProducto}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {PRODUCTOS.map((p) => (
                                            <SelectItem key={p.code} value={p.code}>{p.code}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider text-gray-500">Complejidad</label>
                                <Select value={leadComplejidad} onValueChange={setLeadComplejidad}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="estandar">Estándar · 10d</SelectItem>
                                        <SelectItem value="compleja">Compleja · 20d</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenLeads(false)}>Cancelar</Button>
                        <Button onClick={promover} data-testid="modal-lead-promover">Promover</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
